import React, { useState } from "react"
import { graphql } from "gatsby"
import { getImage } from "gatsby-plugin-image"

import findImage from "@/utils/find-image"

import MainLayout from "@/layouts/MainLayout"
import SharedHead from "@/components/SharedHead"

import ProductHeader from "@/components/ProductHeader"
import G2ReviewsCarousel from "@/components/G2ReviewsCarousel"
import FeatureDemoTabsHeader, {
  FeatureDemoTab,
} from "@/components/FeatureDemoTabsHeader"
import CustomerStorySnapshot from "@/components/CustomerStorySnapshot"
import FeatureDemoNoTabs from "@/components/FeatureDemoNoTabs"
import WebinarSignupBanner from "@/components/WebinarSignupBanner"
import ImageWithTextBlockContainer from "@/components/ImageWithTextBlockContainer"
import PricingSnapshot from "@/components/PricingSnapshot"
import ImageWithTextBlock from "@/components/ImageWithTextBlock"
import FAQ from "@/components/FAQ"
import ExploreSlider from "@/components/ExploreSlider"
import Divider from "@/components/Divider"

// Icons
import TasksAndCalendarIcon from "@/components/TasksAndCalendar"
import SalesPipelineIcon from "@/components/Sales"
import AutomationsIcon from "@/components/Automations"
import ContactManagementIcon from "@/components/ContactManagement"
import ReportingIcon from "@/components/Reporting"

import RolatubeImage from "@/icons/customer-story-logos/rolatube-image.svg"

import {
  LuListTodo,
  LuSquareKanban,
  LuSendHorizontal,
  LuWorkflow,
} from "react-icons/lu"

import { useTranslation } from "@zestia/gatsby-plugin-react-i18next"

import useHeadTranslation from "@/hooks/use-head-translation"

export const Head = props => {
  const { t } = useHeadTranslation(props)

  return (
    <SharedHead
      {...props}
      title={t("project-management-crm.meta-title")}
      description={t("project-management-crm.meta-description")}
    />
  )
}

const ProjectsPage = ({ data }) => {
  const { t } = useTranslation()

  const [tier, setTier] = useState()
  const [frequency, setFrequency] = useState("ANNUAL")

  return (
    <MainLayout
      trackingData={{
        content_category: "feature",
        intent: "tofu",
      }}
    >
      <ProductHeader
        kicker={t("project-management-crm.header.kicker")}
        title={t("project-management-crm.header.title")}
        description={t("project-management-crm.header.description")}
        links={[
          {
            to: "#sales-view",
            text: t("project-management-crm.header.links.faster-sales"),
          },
          {
            to: "#cost-effective",
            text: t("project-management-crm.header.links.client-onboarding"),
          },
          {
            to: "#automated",
            text: t("project-management-crm.header.links.goodbye-admin"),
          },
        ]}
        image={{
          src: findImage(data.allFile, "project-management-header"),
          alt: t("project-management-crm.header.image-alt"),
        }}
      />
      <G2ReviewsCarousel
        reviews={t("home.g2-review-quotes", { returnObjects: true })}
      />

      <FeatureDemoTabsHeader>
        <FeatureDemoTab
          name={t("project-management-crm.featureTabs.tab-1.tab-title")}
          isDefault="true"
          id="sales-view"
          kicker={t("project-management-crm.featureTabs.tab-1.kicker")}
          title={t("project-management-crm.featureTabs.tab-1.title")}
          description={t(
            "project-management-crm.featureTabs.tab-1.description",
          )}
          image={{
            src: findImage(data.allFile, "tab-one"),
            alt: t("project-management-crm.featureTabs.tab-1.image-alt"),
          }}
        />
        <FeatureDemoTab
          name={t("project-management-crm.featureTabs.tab-2.tab-title")}
          id="cost-effective"
          kicker={t("project-management-crm.featureTabs.tab-2.kicker")}
          title={t("project-management-crm.featureTabs.tab-2.title")}
          description={t(
            "project-management-crm.featureTabs.tab-2.description",
          )}
          image={{
            src: findImage(data.allFile, "tab-two"),
            alt: t("project-management-crm.featureTabs.tab-2.image-alt"),
          }}
        />
        <FeatureDemoTab
          name={t("project-management-crm.featureTabs.tab-3.tab-title")}
          id="automated"
          kicker={t("project-management-crm.featureTabs.tab-3.kicker")}
          title={t("project-management-crm.featureTabs.tab-3.title")}
          description={t(
            "project-management-crm.featureTabs.tab-3.description",
          )}
          image={{
            src: findImage(data.allFile, "tab-three"),
            alt: t("project-management-crm.featureTabs.tab-3.image-alt"),
          }}
        />
      </FeatureDemoTabsHeader>

      <CustomerStorySnapshot
        alt={t("customer-story-snapshot.rolatube.alt")}
        name={t("customer-story-snapshot.rolatube.name")}
        title={t("customer-story-snapshot.rolatube.title")}
        companyName={t("customer-story-snapshot.rolatube.company")}
        quote={t("customer-story-snapshot.rolatube.quote")}
        link="/resources/customer-stories/rolatube/"
        backgroundImage={getImage(findImage(data.allFile, "rolatube-bg"))}
        svg={RolatubeImage}
      />

      <Divider />

      <FeatureDemoNoTabs
        kicker={t("project-management-crm.feature-demo.kicker")}
        title={t("project-management-crm.feature-demo.title")}
        description={t("project-management-crm.feature-demo.description")}
        image={getImage(findImage(data.allFile, "main"))}
        imageAlt={t("project-management-crm.feature-demo.image-alt")}
        highlights={[
          {
            title: t("project-management-crm.feature-demo.highlight-1.title"),
            description: t(
              "project-management-crm.feature-demo.highlight-1.description",
            ),
            icon: LuSquareKanban,
          },
          {
            title: t("project-management-crm.feature-demo.highlight-2.title"),
            description: t(
              "project-management-crm.feature-demo.highlight-2.description",
            ),
            icon: LuListTodo,
          },
          {
            title: t("project-management-crm.feature-demo.highlight-3.title"),
            description: t(
              "project-management-crm.feature-demo.highlight-3.description",
            ),
            icon: LuSendHorizontal,
          },
          {
            title: t("project-management-crm.feature-demo.highlight-4.title"),
            description: t(
              "project-management-crm.feature-demo.highlight-4.description",
            ),
            icon: LuWorkflow,
          },
        ]}
      />

      <Divider width="1240" />

      <WebinarSignupBanner />

      <PricingSnapshot
        title={t("project-management-crm.pricing-snapshot.title")}
        description={t("project-management-crm.pricing-snapshot.description")}
        frequency={frequency}
        onSelectPlan={setTier}
        onTogglePlanFrequency={setFrequency}
        showFrequencyToggle={true}
        tier={tier}
        closeModal={() => setTier(null)}
        className="pb-20"
      />

      <Divider width="1240" noTopMargin />
      <ImageWithTextBlockContainer columnLayout={3}>
        <ImageWithTextBlock
          title={t("project-management-crm.why-capsule.real-help.title")}
          text={t("project-management-crm.why-capsule.real-help.description")}
          image={{
            src: getImage(findImage(data.allFile, "support")),
            alt: t("project-management-crm.why-capsule.real-help.alt"),
          }}
        />
        <ImageWithTextBlock
          title={t("project-management-crm.why-capsule.mobile-app.title")}
          text={t("project-management-crm.why-capsule.mobile-app.description")}
          image={{
            src: getImage(findImage(data.allFile, "mobile")),
            alt: t("project-management-crm.why-capsule.mobile-app.alt"),
          }}
        />
        <ImageWithTextBlock
          title={t("project-management-crm.why-capsule.get-started.title")}
          text={t("project-management-crm.why-capsule.get-started.description")}
          image={{
            src: getImage(findImage(data.allFile, "import")),
            alt: t("project-management-crm.why-capsule.get-started.alt"),
          }}
        />
      </ImageWithTextBlockContainer>

      <div className="bg-white pt-20">
        <FAQ
          data={[
            {
              title: t("project-management-crm.faqs.one.question"),
              description: t("project-management-crm.faqs.one.answer"),
            },
            {
              title: t("project-management-crm.faqs.two.question"),
              description: t("project-management-crm.faqs.two.answer"),
            },
            {
              title: t("project-management-crm.faqs.three.question"),
              description: t("project-management-crm.faqs.three.answer"),
            },
            {
              title: t("project-management-crm.faqs.four.question"),
              description: t("project-management-crm.faqs.four.answer"),
            },
          ]}
        />
      </div>
      <ExploreSlider
        slides={[
          {
            title: t(
              "project-management-crm.explore-slider.tasks-and-calendar.title",
            ),
            description: t(
              "project-management-crm.explore-slider.tasks-and-calendar.description",
            ),
            link: "/features/tasks-and-calendar/",
            icon: TasksAndCalendarIcon,
            explore: t(
              "project-management-crm.explore-slider.tasks-and-calendar.explore",
            ),
          },
          {
            title: t("project-management-crm.explore-slider.automations.title"),
            description: t(
              "project-management-crm.explore-slider.automations.description",
            ),
            link: "/features/workflow-automation/",
            icon: AutomationsIcon,
            explore: t(
              "project-management-crm.explore-slider.automations.explore",
            ),
          },
          {
            title: t(
              "project-management-crm.explore-slider.contact-management.title",
            ),
            description: t(
              "project-management-crm.explore-slider.contact-management.description",
            ),
            link: "/features/contact-management-software/",
            icon: ContactManagementIcon,
            explore: t(
              "project-management-crm.explore-slider.contact-management.explore",
            ),
          },
          {
            title: t(
              "project-management-crm.explore-slider.sales-pipeline.title",
            ),
            description: t(
              "project-management-crm.explore-slider.sales-pipeline.description",
            ),
            link: "/features/sales-pipeline/",
            icon: SalesPipelineIcon,
            explore: t(
              "project-management-crm.explore-slider.sales-pipeline.explore",
            ),
          },
          {
            title: t(
              "project-management-crm.explore-slider.sales-reports.title",
            ),
            description: t(
              "project-management-crm.explore-slider.sales-reports.description",
            ),
            link: "/features/sales-analytics/",
            icon: ReportingIcon,
            explore: t(
              "project-management-crm.explore-slider.sales-reports.explore",
            ),
          },
        ]}
      />
    </MainLayout>
  )
}

export const query = graphql`
  query {
    ...Locales

    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: {
          in: [
            "features/project-management-crm"
            "homepage"
            "customer-stories/snapshot-imagery"
          ]
        }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`
export default ProjectsPage
